import { Suspense, lazy, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Pencil, RotateCcw, X } from "lucide-react";
import { ApiError } from "@/api/client";
import { useResetScenarioToLibrary, useScenario, useUpdateScenario } from "@/api/scenarios";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { scenarioFailureKind, scenarioIndustry } from "./scenarioCategories";
import { ScenarioOverview } from "./ScenarioOverview";
import { ScenarioRunForm } from "./ScenarioRunForm";
import type { ScenarioForRun } from "./ScenarioRunForm";

const Monaco = lazy(() =>
  import("@monaco-editor/react").then((m) => ({ default: m.default })),
);

export function ScenarioDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  if (!slug) {
    return (
      <div className="p-6">
        <Link to="/scenarios" className="underline text-sm">
          Back to scenarios
        </Link>
      </div>
    );
  }
  return <DetailPane slug={slug} />;
}

/** Build the ScenarioForRun view-model from a parsed scenario spec. */
function scenarioForRun(slug: string, spec: Record<string, unknown> | null): ScenarioForRun {
  const raw = spec ?? {};
  const streaming = raw.streaming as { supported_backends?: string[] } | undefined;
  type BackendEntry = { backend: string; services?: unknown[] };
  const backends = Array.isArray(raw.backends) ? (raw.backends as BackendEntry[]) : [];
  return { id: slug, streaming, backends };
}

function DetailPane({ slug }: { slug: string }) {
  const scenario = useScenario(slug);
  const update = useUpdateScenario(slug);
  const reset = useResetScenarioToLibrary(slug);
  const [editing, setEditing] = useState(false);
  const [yamlDraft, setYamlDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const permissions = usePermissions();

  useEffect(() => {
    if (scenario.data) setYamlDraft(scenario.data.yaml);
    setEditing(false);
    setError(null);
  }, [scenario.data, slug]);

  if (scenario.isLoading || !scenario.data) {
    return <div className="p-6 text-muted-foreground">Loading…</div>;
  }

  async function onSave() {
    if (!permissions.canMutate) return;
    setError(null);
    try {
      await update.mutateAsync(yamlDraft);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  if (editing) {
    return (
      <div className="p-6 h-full flex flex-col gap-4">
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditing(false);
                setYamlDraft(scenario.data?.yaml ?? "");
                setError(null);
              }}
            >
              <X size={14} /> Cancel
            </Button>
            <h2 className="text-lg font-semibold text-foreground font-mono">{slug}</h2>
          </div>
          <Button onClick={onSave} disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Card className="flex-1 overflow-hidden p-0">
          <Suspense fallback={<div className="p-4 text-muted-foreground">Loading editor…</div>}>
            <Monaco
              height="100%"
              language="yaml"
              value={yamlDraft}
              onChange={(v) => setYamlDraft(v ?? "")}
              theme={document.documentElement.classList.contains("dark") ? "vs-dark" : "vs"}
              options={{ minimap: { enabled: false }, fontSize: 13 }}
            />
          </Suspense>
        </Card>
      </div>
    );
  }

  const spec = (scenario.data.spec ?? {}) as Record<string, unknown>;
  const scenarioMeta = (spec.scenario ?? {}) as Record<string, unknown>;
  const windowMeta = (scenarioMeta.window ?? {}) as Record<string, unknown>;
  const summary = {
    slug: scenario.data.slug,
    name: (scenarioMeta.name as string) ?? scenario.data.slug,
    description: (scenarioMeta.description as string) ?? "",
  };
  const industry = scenarioIndustry(summary);
  const failure = scenarioFailureKind(summary);
  const windowStart = windowMeta.start as string | undefined;
  const windowDuration = windowMeta.duration as string | undefined;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/scenarios">
            <ArrowLeft size={14} /> Scenarios
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          {scenario.data.resettable && (
            <Button
              variant="outline"
              size="sm"
              disabled={!permissions.canMutate || reset.isPending}
              title={
                permissions.canMutate
                  ? "Restore this scenario to the version shipped with the current image."
                  : (permissions.mutationReason ?? "Insufficient permission")
              }
              onClick={() => setResetOpen(true)}
            >
              <RotateCcw size={14} />
              {reset.isPending ? "Resetting…" : "Reset to shipped"}
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={!permissions.canMutate}
            title={permissions.mutationReason ?? "Insufficient permission"}
            onClick={() => setEditing(true)}
          >
            <Pencil size={14} /> Edit YAML
          </Button>
        </div>
      </div>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset {slug} to shipped version?</AlertDialogTitle>
            <AlertDialogDescription>
              Overwrites this scenario's YAML with the version bundled in
              the current deployment. Any changes made in the UI are
              discarded and cannot be recovered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reset.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={reset.isPending}
              onClick={async (e) => {
                e.preventDefault();
                try {
                  await reset.mutateAsync();
                  setResetOpen(false);
                  setError(null);
                } catch (err) {
                  setError(err instanceof ApiError ? err.message : String(err));
                  setResetOpen(false);
                }
              }}
            >
              {reset.isPending ? "Resetting…" : "Reset"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!permissions.canMutate && (
        <p className="text-xs text-muted-foreground">
          {permissions.mutationReason ?? "Insufficient permissions for this action."}
        </p>
      )}

      <header className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={cn(
              "inline-flex items-center h-5 px-2 rounded-full text-[10px] font-medium border uppercase tracking-wide",
              industry.className,
            )}
          >
            {industry.label}
          </span>
          {failure && (
            <span className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-medium border border-border text-muted-foreground uppercase tracking-wide">
              {failure}
            </span>
          )}
        </div>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground font-mono">
            {summary.slug}
          </h1>
          {summary.name && summary.name !== summary.slug && (
            <p className="text-sm text-muted-foreground mt-1">{summary.name}</p>
          )}
        </div>
        {summary.description && (
          <p className="text-base text-foreground max-w-3xl">{summary.description}</p>
        )}
        {(windowStart || windowDuration) && (
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
            {windowStart && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={12} /> {windowStart}
              </span>
            )}
            {windowDuration && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={12} /> {windowDuration} window
              </span>
            )}
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_22rem] gap-6 items-start">
        <div className="min-w-0">
          <ScenarioOverview detail={scenario.data} />
        </div>
        <aside className="lg:sticky lg:top-6">
          <Card className="p-5 space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Start a run</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Push this scenario into a backend and fire its alerts.
              </p>
            </div>
            <ScenarioRunForm
              scenario={scenarioForRun(scenario.data.slug, scenario.data.spec)}
              canMutate={permissions.canMutate}
              mutationReason={permissions.mutationReason}
            />
          </Card>
          <p className="text-xs text-muted-foreground mt-3">
            Started runs show up in{" "}
            <Link to="/runs" className="underline text-foreground">
              Runs
            </Link>
            .
          </p>
        </aside>
      </div>

      {update.isError && (
        <Alert variant="destructive">
          <AlertDescription>Save failed. Switch to the editor to retry.</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
